import type { AuthProviders, CurrentUser } from "@procuremate/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError, api } from "@/lib/api-client";

export const authKeys = {
  me: ["auth", "me"] as const,
  providers: ["auth", "providers"] as const,
};

/** Sign-in methods the API offers (Microsoft when Entra is configured). */
export function useAuthProviders() {
  return useQuery({
    queryKey: authKeys.providers,
    queryFn: () => api<AuthProviders>("/auth/providers"),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** The signed-in user, or null when there is no valid session. */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: async () => {
      try {
        return await api<CurrentUser>("/auth/me");
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Stores the user returned by login / invite acceptance so guarded routes render immediately. */
export function useSetCurrentUser() {
  const queryClient = useQueryClient();
  return (user: CurrentUser | null) => queryClient.setQueryData(authKeys.me, user);
}

/** Saves the header language on the account: notification emails are written in it. */
export function useSaveLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: CurrentUser["locale"]) =>
      api<CurrentUser>("/auth/me/locale", { method: "PUT", body: { locale } }),
    onSuccess: (user) => queryClient.setQueryData(authKeys.me, user),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSettled: () => {
      queryClient.setQueryData(authKeys.me, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
    },
  });
}

/**
 * Only same-app paths are allowed as post-login destinations
 * (prevents open redirects such as ?next=//evil.example).
 */
export function safeNextPath(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}
